import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { toJSON } from '@atlaskit/editor-common/utils';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports

import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { AllSelection } from '@atlaskit/editor-prosemirror/state';
import { Flex } from '@atlaskit/primitives/compiled';
import { TextSerializer } from '@atlaskit/renderer/text-serializer';
import { LinkPickerButton } from '../components/LinkPickerButton/LinkPickerButton';
import { MentionButton } from '../components/MentionButton/MentionButton';
import { createAIPromptPreset } from '../presets/prompt';

// PromptEditorProps is mirrored at `packages/editor/generative-ai-modal/src/ui/components/PromptEditorWrapper/PromptEditorWrapper.tsx`
// since editor-ai-injected-editors cannot be a dependency of generative-ai-modal or editor-plugin-ai.

var EditorWrapper = function EditorWrapper(props) {
  var _editorProps$autoFocu;
  var appearance = 'chromeless';
  var editorProps = props.editorProps,
    options = props.options;
  var _editorProps$type = editorProps.type,
    type = _editorProps$type === void 0 ? 'user-input' : _editorProps$type,
    onChange = editorProps.onChange,
    onInputChange = editorProps.onInputChange,
    onADFChange = editorProps.onADFChange,
    defaultValue = editorProps.defaultValue,
    enableLinks = editorProps.enableLinks,
    setFocusHandlers = editorProps.setFocusHandlers,
    setClearHandler = editorProps.setClearHandler,
    ariaLabel = editorProps.ariaLabel;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var handleChange = useCallback(function (editorView) {
    var textSerializer = TextSerializer.fromSchema(editorView.state.schema);
    var text = textSerializer.serializeFragment(editorView.state.doc.content);
    var adf = toJSON(editorView.state.doc);
    onChange === null || onChange === void 0 || onChange({
      text: text,
      adf: adf
    });
    onADFChange === null || onADFChange === void 0 || onADFChange(adf);
    onInputChange === null || onInputChange === void 0 || onInputChange(text);
  }, [onChange, onADFChange, onInputChange]);
  var onSave = useCallback(function (editorView) {
    handleChange(editorView);
  }, [handleChange]);

  // We could have created preset outside PromptEditor component,
  //  but currently placeholder prop ComposableEditor is not working.
  // We have to pass placeholder to placeholder plugin.
  // That's why using usePreset, who memoize preset.
  var _usePreset = usePreset(function () {
      return createAIPromptPreset(_objectSpread(_objectSpread({}, options), {}, {
        placeholder: {
          placeholder: editorProps.placeholder
        },
        onSave: onSave,
        enableLinks: enableLinks
      }));
    }, [options, editorProps.placeholder]),
    preset = _usePreset.preset,
    editorApi = _usePreset.editorApi;
  var editorViewRef = useRef(null);
  var onEditorReady = useCallback(function (editorActions) {
    if (setClearHandler) {
      setClearHandler(function () {
        return editorActions.clear();
      });
    }
    var editorView = editorActions._privateGetEditorView();
    if (editorView) {
      editorViewRef.current = editorView;
      if (!editorActions.isDocumentEmpty()) {
        editorView.dispatch(editorView.state.tr.setSelection(new AllSelection(editorView.state.doc)));
      }
    }
  }, [setClearHandler]);
  useEffect(function () {
    if (setFocusHandlers) {
      var _editorApi$core, _editorApi$core2;
      /**
       * We need to expose clear function through ref because
       *  changing defaultValue does not clear editor.
       */
      setFocusHandlers(editorApi === null || editorApi === void 0 || (_editorApi$core = editorApi.core) === null || _editorApi$core === void 0 ? void 0 : _editorApi$core.actions.blur, editorApi === null || editorApi === void 0 || (_editorApi$core2 = editorApi.core) === null || _editorApi$core2 === void 0 ? void 0 : _editorApi$core2.actions.focus);
    }
  }, [editorApi, setFocusHandlers]);

  // Skip any toolbars that displays in the editor, like Link Picker toolbar
  // Yes, it's ugly, but it's easier way, because we can't have access to the PromptEditor
  // floating toolbar.
  var stopPropagationForFloatingToolbar = useCallback(function (event) {
    var eventFloatingToolbar = event.target instanceof HTMLElement ? event.target.closest('[data-testid="editor-floating-toolbar"]') : null;

    // Check that floating toolbar is a child of the current target,
    // i.e. it's a child of current the editor.
    if (eventFloatingToolbar && event.currentTarget.contains(eventFloatingToolbar)) {
      event.stopPropagation();
    }
  }, []);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", {
      onKeyDown: stopPropagationForFloatingToolbar,
      onKeyUp: stopPropagationForFloatingToolbar
    }, /*#__PURE__*/React.createElement(Flex, {
      gap: "space.050"
    }, /*#__PURE__*/React.createElement(ComposableEditor, {
      mentionProvider: options.mentionProvider,
      appearance: appearance,
      minHeight: 20,
      maxHeight: 200,
      preset: preset,
      assistiveLabel: ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : formatMessage(aiMessages.promptbuilderAriaLabel),
      shouldFocus: (_editorProps$autoFocu = editorProps.autoFocus) !== null && _editorProps$autoFocu !== void 0 ? _editorProps$autoFocu : true,
      defaultValue: defaultValue,
      onChange: handleChange
      // We will remove use of onEditorReady when editorApi provides a matching "clear()" action.
      ,
      onEditorReady: onEditorReady
    }), (type === 'user-input' || type === 'interrogate') && /*#__PURE__*/React.createElement(React.Fragment, null, options.enableLinks && options.linkPicker && /*#__PURE__*/React.createElement(LinkPickerButton, {
      editorApi: editorApi,
      promptType: type
    }), options.mentionProvider && /*#__PURE__*/React.createElement(MentionButton, {
      editorApi: editorApi,
      promptType: type
    }))))
  );
};
export function createPromptEditor(options) {
  return function (props) {
    var editorProps = {
      editorProps: props,
      options: options
    };
    return /*#__PURE__*/React.createElement(EditorContext, null, /*#__PURE__*/React.createElement(EditorWrapper
    // Ignored via go/ees005
    // eslint-disable-next-line react/jsx-props-no-spreading
    , editorProps));
  };
}